/**
 * Per-field write ownership for the contract detail view.
 *
 * Every optimistic write claims the fields it sends. When its response lands —
 * success or failure — it may only touch the fields it still owns, because a
 * newer write may have claimed some of them in the meantime.
 *
 * Extracted from ContractDetailedView so the ordering logic can be tested: the
 * component itself has no test harness, and the interleavings below are exactly
 * where this went wrong once already.
 *
 * Why this exists rather than `services/hooks/entities/useFieldUpdates`, which
 * every other detail view uses: that hook serialises writes through a queue
 * keyed by entity id, and the contract view needs three things it does not
 * model — the currency write that also patches the linked deal, the
 * onboardingStageId edit that bypasses the PUT entirely for the stage-move
 * endpoint, and the rule that `dealId` may only ride along on the currency
 * flow so other edits do not trip the financials permission check. Reworking
 * the contract view onto the queue is the better end state and is deliberately
 * not attempted here.
 *
 * The cost of keeping both: the refs in ContractDetailedView follow the
 * component, not the record, so unlike `useFieldUpdates` there is no entity-id
 * guard. That is safe only while the view cannot swap contracts without
 * remounting — true today; the one cross-contract link reaches it through
 * DuplicateContractPage, which unmounts.
 */

/** Which write last claimed each field, keyed by field name. */
export interface FieldWriteIds {
  [fieldName: string]: number;
}

/**
 * The keys a write may still act on: those no later write has since claimed.
 *
 * Writing a key a newer write owns would discard that newer value — on failure
 * by reverting it, on success by overwriting it with an older server state.
 */
export const keysOwnedBy = (
  writtenKeys: string[],
  fieldWriteIds: FieldWriteIds,
  writeId: number
): string[] => writtenKeys.filter((key) => fieldWriteIds[key] === writeId);

/**
 * The keys whose confirmed baseline this write's response should advance.
 *
 * Distinct from `keysOwnedBy`, and the distinction is the whole point. Ownership
 * governs the VISIBLE state: a write may not paint a field a newer write has
 * claimed. Confirmation governs the ROLLBACK BASELINE, and a superseded write's
 * response is still the newest thing the server has told us about that field —
 * so it must be recorded even though it may not be shown. Without this, two
 * writes to one field where the first succeeds and the second fails roll the
 * field back past a value the server accepted, with nothing left to correct it.
 *
 * Ordered by writeId so an older response landing late cannot overwrite a newer
 * confirmation.
 */
export const keysToConfirm = (
  writtenKeys: string[],
  confirmedWriteIds: FieldWriteIds,
  writeId: number
): string[] =>
  writtenKeys.filter((key) => (confirmedWriteIds[key] ?? 0) < writeId);

/**
 * Copy `keys` from `source` onto `target`, leaving every other field alone.
 *
 * Used both ways round: a failure copies from the last confirmed contract, a
 * late success copies from the server's response. Taking the whole object
 * instead would clobber fields a concurrent write owns.
 */
export const applyFields = <T extends object>(
  target: T,
  source: T,
  keys: string[]
): T => ({
  ...target,
  ...Object.fromEntries(keys.map((key) => [key, source[key as keyof T]]))
});
